import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  VStack,
  HStack,
  Checkbox,
  Divider,
  Text,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';
import { parseDateString, formatDateToString } from 'utils/helpers';
import { useAnswerCodes, useValidationSchema } from '../hooks';
import { ValidatedField } from '../components';
import OilTanksModal from './OilTanksModal';
import type { Location, OilTank } from 'api';

interface AuxiliaryHeatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: Location;
  onSave: (data: Partial<Location>) => void;
  isUpdating?: boolean;
  itvLockedFields?: string[];
}

interface AuxiliaryHeatingData {
  auxiliary_heating_type_installed: string;
  other_auxiliary_heating_type: string;
  auxiliary_heating_fuel_type: string;
  other_auxiliary_heating_fuel_type: string;
  auxiliary_heating_approved_by: string;
  auxiliary_heating_professionally_installed: string;
  auxiliary_heating_fireplace_type: string;
  second_auxiliary_heating_fuel_type: string;
  other_second_auxiliary_heating_fuel_type: string;
  auxiliary_heating_wett_certified: string;
  auxiliary_heating_wett_certification_number: string;
  auxiliary_heating_wett_inspection_date: string | null;
  auxiliary_heating_fuel_type_number_of_face_cords_per_year: number | null;
  auxiliary_heating_year_installed: number | null;
  auxiliary_heating_percentage_renovated: number | null;
  auxiliary_heating_automatic_fire_extinguisher: boolean;
  chimney_exists: string;
  chimney_type: string;
  other_chimney_type: string;
  chimney_built_from: string;
  chimney_built_from_other: string;
  chimney_lining: string;
  chimney_lining_other: string;
  chimney_location: string;
  chimney_location_other: string;
  chimney_professionally_cleaned: string;
  frequency_of_chimney_cleaning: string;
  stove_location: string;
  stove_location_other: string;
  woodstove_installed_date: string | null;
  number_of_hours_used_per_day: number | null;
  number_of_days_used_per_year: number | null;
}

const AuxiliaryHeatingModal = ({
  isOpen,
  onClose,
  location,
  onSave,
  isUpdating,
  itvLockedFields = [],
}: AuxiliaryHeatingModalProps) => {
  const {
    getOptions,
    getLabel,
    getCodeByLabel,
    shouldShowConditionalField,
    shouldShowConditionalFieldForHeatingSource,
    isHeatingTypeNone,
    getValidFuelOptions,
    answerCodes,
  } = useAnswerCodes();
  const { validateEntity } = useValidationSchema();
  const oilTanksModal = useDisclosure();

  const isFieldLocked = (field: string) => itvLockedFields.includes(field);
  const [oilTanks, setOilTanks] = useState<OilTank[]>([]);
  const [data, setData] = useState<AuxiliaryHeatingData>({
    auxiliary_heating_type_installed: '',
    other_auxiliary_heating_type: '',
    auxiliary_heating_fuel_type: '',
    other_auxiliary_heating_fuel_type: '',
    auxiliary_heating_approved_by: '',
    auxiliary_heating_professionally_installed: '',
    auxiliary_heating_fireplace_type: '',
    second_auxiliary_heating_fuel_type: '',
    other_second_auxiliary_heating_fuel_type: '',
    auxiliary_heating_wett_certified: '',
    auxiliary_heating_wett_certification_number: '',
    auxiliary_heating_wett_inspection_date: null,
    auxiliary_heating_fuel_type_number_of_face_cords_per_year: null,
    auxiliary_heating_year_installed: null,
    auxiliary_heating_percentage_renovated: null,
    auxiliary_heating_automatic_fire_extinguisher: false,
    chimney_exists: '',
    chimney_type: '',
    other_chimney_type: '',
    chimney_built_from: '',
    chimney_built_from_other: '',
    chimney_lining: '',
    chimney_lining_other: '',
    chimney_location: '',
    chimney_location_other: '',
    chimney_professionally_cleaned: '',
    frequency_of_chimney_cleaning: '',
    stove_location: '',
    stove_location_other: '',
    woodstove_installed_date: null,
    number_of_hours_used_per_day: null,
    number_of_days_used_per_year: null,
  });

  useEffect(() => {
    if (isOpen) {
      setData({
        auxiliary_heating_type_installed:
          location.auxiliary_heating_type_installed || '',
        other_auxiliary_heating_type:
          location.other_auxiliary_heating_type || '',
        auxiliary_heating_fuel_type: location.auxiliary_heating_fuel_type || '',
        other_auxiliary_heating_fuel_type:
          location.other_auxiliary_heating_fuel_type || '',
        auxiliary_heating_approved_by:
          location.auxiliary_heating_approved_by || '',
        auxiliary_heating_professionally_installed:
          location.auxiliary_heating_professionally_installed || '',
        auxiliary_heating_fireplace_type:
          location.auxiliary_heating_fireplace_type || '',
        second_auxiliary_heating_fuel_type:
          location.second_auxiliary_heating_fuel_type || '',
        other_second_auxiliary_heating_fuel_type:
          location.other_second_auxiliary_heating_fuel_type || '',
        auxiliary_heating_wett_certified:
          location.auxiliary_heating_wett_certified || '',
        auxiliary_heating_wett_certification_number:
          location.auxiliary_heating_wett_certification_number || '',
        auxiliary_heating_wett_inspection_date:
          location.auxiliary_heating_wett_inspection_date || null,
        auxiliary_heating_fuel_type_number_of_face_cords_per_year:
          location.auxiliary_heating_fuel_type_number_of_face_cords_per_year ??
          null,
        auxiliary_heating_year_installed:
          location.auxiliary_heating_year_installed ?? null,
        auxiliary_heating_percentage_renovated:
          location.auxiliary_heating_percentage_renovated ?? null,
        auxiliary_heating_automatic_fire_extinguisher:
          location.auxiliary_heating_automatic_fire_extinguisher || false,
        chimney_exists: location.chimney_exists || '',
        chimney_type: location.chimney_type || '',
        other_chimney_type: location.other_chimney_type || '',
        chimney_built_from: location.chimney_built_from || '',
        chimney_built_from_other: location.chimney_built_from_other || '',
        chimney_lining: location.chimney_lining || '',
        chimney_lining_other: location.chimney_lining_other || '',
        chimney_location: location.chimney_location || '',
        chimney_location_other: location.chimney_location_other || '',
        chimney_professionally_cleaned:
          location.chimney_professionally_cleaned || '',
        frequency_of_chimney_cleaning:
          location.frequency_of_chimney_cleaning || '',
        stove_location: location.stove_location || '',
        stove_location_other: location.stove_location_other || '',
        woodstove_installed_date: location.woodstove_installed_date || null,
        number_of_hours_used_per_day:
          location.number_of_hours_used_per_day ?? null,
        number_of_days_used_per_year:
          location.number_of_days_used_per_year ?? null,
      });
      setOilTanks(location.oil_tanks || []);
    }
  }, [isOpen, location]);

  const showOtherHeatingType = shouldShowConditionalField(
    'other_auxiliary_heating_type',
    data.auxiliary_heating_type_installed,
  );
  const showOtherFuelType = shouldShowConditionalField(
    'other_auxiliary_heating_fuel_type',
    data.auxiliary_heating_fuel_type,
  );
  const showApprovedBy = shouldShowConditionalField(
    'auxiliary_heating_approved_by',
    data.auxiliary_heating_type_installed,
  );
  const showProfessionallyInstalled = shouldShowConditionalField(
    'auxiliary_heating_professionally_installed',
    data.auxiliary_heating_type_installed,
  );
  const showFireplaceType = shouldShowConditionalField(
    'auxiliary_heating_fireplace_type',
    data.auxiliary_heating_type_installed,
  );
  const showSecondFuelType = shouldShowConditionalField(
    'second_auxiliary_heating_fuel_type',
    data.auxiliary_heating_type_installed,
  );
  const showOtherSecondFuelType = shouldShowConditionalField(
    'other_second_auxiliary_heating_fuel_type',
    data.second_auxiliary_heating_fuel_type,
  );
  const showWettCertified = shouldShowConditionalField(
    'auxiliary_heating_wett_certified',
    data as unknown as Record<string, unknown>,
  );
  const showWettDetails = shouldShowConditionalField(
    'auxiliary_heating_wett_certification_number',
    data.auxiliary_heating_wett_certified,
  );
  const showFaceCords = shouldShowConditionalField(
    'auxiliary_heating_fuel_type_number_of_face_cords_per_year',
    data as unknown as Record<string, unknown>,
  );

  // Chimney/stove fields: show in auxiliary modal ONLY when auxiliary triggers AND primary does NOT
  const auxiliaryTriggersChimney = shouldShowConditionalFieldForHeatingSource(
    'chimney_exists',
    data as unknown as Record<string, unknown>,
    'auxiliary',
  );
  const primaryTriggersChimney = shouldShowConditionalFieldForHeatingSource(
    'chimney_exists',
    location as unknown as Record<string, unknown>,
    'primary',
  );
  const showChimneySection =
    auxiliaryTriggersChimney && !primaryTriggersChimney;

  const auxiliaryTriggersStove = shouldShowConditionalFieldForHeatingSource(
    'stove_location',
    data as unknown as Record<string, unknown>,
    'auxiliary',
  );
  const primaryTriggersStove = shouldShowConditionalFieldForHeatingSource(
    'stove_location',
    location as unknown as Record<string, unknown>,
    'primary',
  );
  const showStoveSection = auxiliaryTriggersStove && !primaryTriggersStove;

  // Oil tanks: show when auxiliary triggers oil
  const auxiliaryTriggersOil = shouldShowConditionalFieldForHeatingSource(
    'number_of_oil_storage_tanks',
    data as unknown as Record<string, unknown>,
    'auxiliary',
  );

  const auxiliaryAssociationCode = useMemo(
    () =>
      getCodeByLabel('Auxiliary', 'oil_storage_tank_heating_associated_with'),
    [getCodeByLabel],
  );

  const auxiliaryOilTanks = useMemo(() => {
    return oilTanks.filter(
      (t) => t.heating_associated_with === auxiliaryAssociationCode,
    );
  }, [oilTanks, auxiliaryAssociationCode]);

  const isAuxiliaryOilTanksIncomplete = useMemo(() => {
    return auxiliaryOilTanks.some(
      (tank) =>
        validateEntity(
          'location/oil_tank',
          tank as unknown as Record<string, unknown>,
        ).length > 0,
    );
  }, [auxiliaryOilTanks, validateEntity]);

  const handleOilTanksSave = (tanks: OilTank[]) => {
    setOilTanks(tanks);
  };

  const chimneyExistsYes = (): boolean => {
    if (!data.chimney_exists) return false;
    const label = getLabel(data.chimney_exists, 'chimney_exists');
    return label.toLowerCase() === 'yes';
  };

  const isChimneyTypeOther = (): boolean => {
    if (!data.chimney_type) return false;
    const label = getLabel(data.chimney_type, 'chimney_type');
    return label.toLowerCase() === 'other';
  };

  const isChimneyBuiltFromOther = (): boolean => {
    if (!data.chimney_built_from) return false;
    const label = getLabel(data.chimney_built_from, 'chimney_built_from');
    return label.toLowerCase() === 'other';
  };

  const isChimneyLiningOther = (): boolean => {
    if (!data.chimney_lining) return false;
    const label = getLabel(data.chimney_lining, 'chimney_lining');
    return label.toLowerCase() === 'other';
  };

  const isChimneyLocationOther = (): boolean => {
    if (!data.chimney_location) return false;
    const label = getLabel(data.chimney_location, 'chimney_location');
    return label.toLowerCase() === 'other';
  };

  const isStoveLocationOther = (): boolean => {
    if (!data.stove_location) return false;
    const label = getLabel(data.stove_location, 'stove_location');
    return label.toLowerCase() === 'other';
  };

  const handleSelectChange =
    (
      field: keyof AuxiliaryHeatingData,
      dependentFields?: (keyof AuxiliaryHeatingData)[],
    ) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setData((prev) => {
        const updated = { ...prev, [field]: e.target.value };
        if (dependentFields) {
          for (const depField of dependentFields) {
            (updated as Record<string, unknown>)[depField] = '';
          }
        }

        if (field === 'auxiliary_heating_type_installed') {
          if (isHeatingTypeNone(e.target.value)) {
            updated.auxiliary_heating_fuel_type = '';
            updated.other_auxiliary_heating_fuel_type = '';
            updated.auxiliary_heating_fuel_type_number_of_face_cords_per_year =
              null;
            updated.auxiliary_heating_approved_by = '';
            updated.auxiliary_heating_professionally_installed = '';
            updated.auxiliary_heating_wett_certified = '';
            updated.auxiliary_heating_wett_certification_number = '';
            updated.auxiliary_heating_wett_inspection_date = null;
          } else {
            const newHeatingType = e.target.value;
            const validFuels =
              answerCodes?.heating_fuel_mapping?.[newHeatingType];

            if (
              validFuels &&
              prev.auxiliary_heating_fuel_type &&
              !validFuels.includes(prev.auxiliary_heating_fuel_type)
            ) {
              updated.auxiliary_heating_fuel_type = '';
              updated.other_auxiliary_heating_fuel_type = '';
              updated.auxiliary_heating_fuel_type_number_of_face_cords_per_year =
                null;
            }
          }
        }

        return updated;
      });
    };

  const handleTextChange =
    (field: keyof AuxiliaryHeatingData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleNumberChange =
    (field: keyof AuxiliaryHeatingData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      setData((prev) => ({
        ...prev,
        [field]: isNaN(value as number) ? null : value,
      }));
    };

  const handleCheckboxChange =
    (field: keyof AuxiliaryHeatingData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData((prev) => ({ ...prev, [field]: e.target.checked }));
    };

  const handleDateChange =
    (field: keyof AuxiliaryHeatingData) => (date: Date | null) => {
      setData((prev) => ({ ...prev, [field]: formatDateToString(date) }));
    };

  const handleSave = () => {
    onSave({ ...data, oil_tanks: oilTanks });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Auxiliary Heating</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <ValidatedField
              itemPath="location"
              fieldPath="auxiliary_heating_type_installed"
              label="Heating Type"
              entity={data as unknown as Record<string, unknown>}
              isDisabled={
                isUpdating || isFieldLocked('auxiliary_heating_type_installed')
              }
            >
              <Select
                variant="main"
                size="sm"
                value={data.auxiliary_heating_type_installed || ''}
                onChange={handleSelectChange(
                  'auxiliary_heating_type_installed',
                  [
                    'other_auxiliary_heating_type',
                    'auxiliary_heating_fireplace_type',
                    'second_auxiliary_heating_fuel_type',
                  ],
                )}
                isDisabled={
                  isUpdating ||
                  isFieldLocked('auxiliary_heating_type_installed')
                }
              >
                {getOptions('auxiliary_heating_type_installed').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>

            {showOtherHeatingType && (
              <FormControl>
                <FormLabel fontSize="sm">Specify Heating Type</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={data.other_auxiliary_heating_type || ''}
                  onChange={handleTextChange('other_auxiliary_heating_type')}
                  isDisabled={isUpdating}
                />
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Fuel Type</FormLabel>
              <Select
                variant="main"
                size="sm"
                value={data.auxiliary_heating_fuel_type || ''}
                onChange={handleSelectChange('auxiliary_heating_fuel_type', [
                  'other_auxiliary_heating_fuel_type',
                ])}
                isDisabled={
                  isUpdating || isFieldLocked('auxiliary_heating_fuel_type')
                }
              >
                {getValidFuelOptions(
                  data.auxiliary_heating_type_installed || '',
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {showOtherFuelType && (
              <FormControl>
                <FormLabel fontSize="sm">Specify Fuel Type</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={data.other_auxiliary_heating_fuel_type || ''}
                  onChange={handleTextChange(
                    'other_auxiliary_heating_fuel_type',
                  )}
                  isDisabled={isUpdating}
                />
              </FormControl>
            )}

            {showApprovedBy && (
              <FormControl>
                <FormLabel fontSize="sm">Approved By</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={data.auxiliary_heating_approved_by || ''}
                  onChange={handleSelectChange('auxiliary_heating_approved_by')}
                  isDisabled={isUpdating}
                >
                  {getOptions('auxiliary_heating_approved_by').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {showProfessionallyInstalled && (
              <FormControl>
                <FormLabel fontSize="sm">Professionally Installed</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={data.auxiliary_heating_professionally_installed || ''}
                  onChange={handleSelectChange(
                    'auxiliary_heating_professionally_installed',
                  )}
                  isDisabled={isUpdating}
                >
                  {getOptions('auxiliary_heating_professionally_installed').map(
                    (opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ),
                  )}
                </Select>
              </FormControl>
            )}

            {showFireplaceType && (
              <FormControl>
                <FormLabel fontSize="sm">Fireplace Type</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={data.auxiliary_heating_fireplace_type || ''}
                  onChange={handleSelectChange(
                    'auxiliary_heating_fireplace_type',
                  )}
                  isDisabled={isUpdating}
                >
                  {getOptions('auxiliary_heating_fireplace_type').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {showSecondFuelType && (
              <>
                <FormControl>
                  <FormLabel fontSize="sm">
                    Second Fuel Type (Bi-Energy)
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={data.second_auxiliary_heating_fuel_type || ''}
                    onChange={handleSelectChange(
                      'second_auxiliary_heating_fuel_type',
                      ['other_second_auxiliary_heating_fuel_type'],
                    )}
                    isDisabled={isUpdating}
                  >
                    {getOptions('second_auxiliary_heating_fuel_type').map(
                      (opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ),
                    )}
                  </Select>
                </FormControl>

                {showOtherSecondFuelType && (
                  <FormControl>
                    <FormLabel fontSize="sm">
                      Specify Second Fuel Type
                    </FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      value={
                        data.other_second_auxiliary_heating_fuel_type || ''
                      }
                      onChange={handleTextChange(
                        'other_second_auxiliary_heating_fuel_type',
                      )}
                      isDisabled={isUpdating}
                    />
                  </FormControl>
                )}
              </>
            )}

            {showWettCertified && (
              <>
                <Divider />
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  WETT Certification
                </Text>

                <FormControl>
                  <FormLabel fontSize="sm">WETT Certified</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={data.auxiliary_heating_wett_certified || ''}
                    onChange={handleSelectChange(
                      'auxiliary_heating_wett_certified',
                      [
                        'auxiliary_heating_wett_certification_number',
                        'auxiliary_heating_wett_inspection_date',
                      ],
                    )}
                    isDisabled={isUpdating}
                  >
                    {getOptions('auxiliary_heating_wett_certified').map(
                      (opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ),
                    )}
                  </Select>
                </FormControl>

                {showWettDetails && (
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Certification Number</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={
                          data.auxiliary_heating_wett_certification_number || ''
                        }
                        onChange={handleTextChange(
                          'auxiliary_heating_wett_certification_number',
                        )}
                        isDisabled={isUpdating}
                      />
                    </FormControl>

                    <DateField
                      label="Inspection Date"
                      date={parseDateString(
                        data.auxiliary_heating_wett_inspection_date,
                      )}
                      onChange={handleDateChange(
                        'auxiliary_heating_wett_inspection_date',
                      )}
                      isDisabled={isUpdating}
                      maxDate={new Date()}
                      size="sm"
                    />
                  </SimpleGrid>
                )}
              </>
            )}

            {showFaceCords && (
              <FormControl>
                <FormLabel fontSize="sm">Face Cords per Year</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  min={0}
                  value={
                    data.auxiliary_heating_fuel_type_number_of_face_cords_per_year ??
                    ''
                  }
                  onChange={handleNumberChange(
                    'auxiliary_heating_fuel_type_number_of_face_cords_per_year',
                  )}
                  isDisabled={isUpdating}
                />
              </FormControl>
            )}

            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Year Installed</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={data.auxiliary_heating_year_installed ?? ''}
                  onChange={handleNumberChange(
                    'auxiliary_heating_year_installed',
                  )}
                  isDisabled={isUpdating}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">% Update Completed</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  min={0}
                  max={100}
                  value={data.auxiliary_heating_percentage_renovated ?? ''}
                  onChange={handleNumberChange(
                    'auxiliary_heating_percentage_renovated',
                  )}
                  isDisabled={isUpdating}
                />
              </FormControl>
            </SimpleGrid>

            <Checkbox
              isChecked={
                data.auxiliary_heating_automatic_fire_extinguisher ?? false
              }
              onChange={handleCheckboxChange(
                'auxiliary_heating_automatic_fire_extinguisher',
              )}
              isDisabled={isUpdating}
            >
              Automatic Fire Extinguisher
            </Checkbox>

            {showChimneySection && (
              <>
                <Divider />
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Chimney Details
                </Text>

                <FormControl>
                  <FormLabel fontSize="sm">Chimney Exists</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={data.chimney_exists || ''}
                    onChange={handleSelectChange('chimney_exists')}
                    isDisabled={isUpdating}
                  >
                    {getOptions('chimney_exists').map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {chimneyExistsYes() && (
                  <>
                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Chimney Type</FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.chimney_type || ''}
                          onChange={handleSelectChange('chimney_type')}
                          isDisabled={isUpdating}
                        >
                          {getOptions('chimney_type').map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      {isChimneyTypeOther() && (
                        <FormControl>
                          <FormLabel fontSize="sm">Specify Type</FormLabel>
                          <Input
                            variant="main"
                            size="sm"
                            value={data.other_chimney_type || ''}
                            onChange={handleTextChange('other_chimney_type')}
                            isDisabled={isUpdating}
                          />
                        </FormControl>
                      )}
                    </SimpleGrid>

                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Built From</FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.chimney_built_from || ''}
                          onChange={handleSelectChange('chimney_built_from')}
                          isDisabled={isUpdating}
                        >
                          {getOptions('chimney_built_from').map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      {isChimneyBuiltFromOther() && (
                        <FormControl>
                          <FormLabel fontSize="sm">Specify Material</FormLabel>
                          <Input
                            variant="main"
                            size="sm"
                            value={data.chimney_built_from_other || ''}
                            onChange={handleTextChange(
                              'chimney_built_from_other',
                            )}
                            isDisabled={isUpdating}
                          />
                        </FormControl>
                      )}
                    </SimpleGrid>

                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Lining</FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.chimney_lining || ''}
                          onChange={handleSelectChange('chimney_lining')}
                          isDisabled={isUpdating}
                        >
                          {getOptions('chimney_lining').map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      {isChimneyLiningOther() && (
                        <FormControl>
                          <FormLabel fontSize="sm">Specify Lining</FormLabel>
                          <Input
                            variant="main"
                            size="sm"
                            value={data.chimney_lining_other || ''}
                            onChange={handleTextChange('chimney_lining_other')}
                            isDisabled={isUpdating}
                          />
                        </FormControl>
                      )}
                    </SimpleGrid>

                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Location</FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.chimney_location || ''}
                          onChange={handleSelectChange('chimney_location')}
                          isDisabled={isUpdating}
                        >
                          {getOptions('chimney_location').map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      {isChimneyLocationOther() && (
                        <FormControl>
                          <FormLabel fontSize="sm">Specify Location</FormLabel>
                          <Input
                            variant="main"
                            size="sm"
                            value={data.chimney_location_other || ''}
                            onChange={handleTextChange(
                              'chimney_location_other',
                            )}
                            isDisabled={isUpdating}
                          />
                        </FormControl>
                      )}
                    </SimpleGrid>

                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">
                          Professionally Cleaned
                        </FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.chimney_professionally_cleaned || ''}
                          onChange={handleSelectChange(
                            'chimney_professionally_cleaned',
                          )}
                          isDisabled={isUpdating}
                        >
                          {getOptions('chimney_professionally_cleaned').map(
                            (opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ),
                          )}
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Cleaning Frequency</FormLabel>
                        <Select
                          variant="main"
                          size="sm"
                          value={data.frequency_of_chimney_cleaning || ''}
                          onChange={handleSelectChange(
                            'frequency_of_chimney_cleaning',
                          )}
                          isDisabled={isUpdating}
                        >
                          {getOptions('frequency_of_chimney_cleaning').map(
                            (opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ),
                          )}
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                  </>
                )}
              </>
            )}

            {showStoveSection && (
              <>
                <Divider />
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Wood Stove Details
                </Text>

                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Stove Location</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={data.stove_location || ''}
                      onChange={handleSelectChange('stove_location')}
                      isDisabled={isUpdating}
                    >
                      {getOptions('stove_location').map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {isStoveLocationOther() && (
                    <FormControl>
                      <FormLabel fontSize="sm">Specify Location</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={data.stove_location_other || ''}
                        onChange={handleTextChange('stove_location_other')}
                        isDisabled={isUpdating}
                      />
                    </FormControl>
                  )}
                </SimpleGrid>

                <SimpleGrid columns={2} spacing={4}>
                  <DateField
                    label="Woodstove Installed Date"
                    date={parseDateString(data.woodstove_installed_date)}
                    onChange={handleDateChange('woodstove_installed_date')}
                    isDisabled={isUpdating}
                    maxDate={new Date()}
                    size="sm"
                  />

                  <FormControl>
                    <FormLabel fontSize="sm">Hours Used Per Day</FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      type="number"
                      min={0}
                      max={24}
                      value={data.number_of_hours_used_per_day ?? ''}
                      onChange={handleNumberChange(
                        'number_of_hours_used_per_day',
                      )}
                      isDisabled={isUpdating}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel fontSize="sm">Days Used Per Year</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    min={0}
                    max={365}
                    value={data.number_of_days_used_per_year ?? ''}
                    onChange={handleNumberChange(
                      'number_of_days_used_per_year',
                    )}
                    isDisabled={isUpdating}
                  />
                </FormControl>
              </>
            )}

            {auxiliaryTriggersOil && (
              <>
                <Divider />
                <Text fontWeight="semibold" fontSize="sm">
                  Auxiliary Oil Tanks
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={oilTanksModal.onOpen}
                  isDisabled={isUpdating}
                  borderColor={
                    isAuxiliaryOilTanksIncomplete ? 'red.400' : undefined
                  }
                  w="full"
                >
                  <HStack spacing={2}>
                    <Text>
                      Oil Tanks
                      {auxiliaryOilTanks.length > 0
                        ? ` (${auxiliaryOilTanks.length})`
                        : ''}
                    </Text>
                    {isAuxiliaryOilTanksIncomplete && (
                      <Icon as={WarningIcon} color="red.400" boxSize={3} />
                    )}
                  </HStack>
                </Button>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <SimpleGrid columns={2} spacing={3} w="full">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSave}
              isDisabled={isUpdating}
            >
              Save
            </Button>
          </SimpleGrid>
        </ModalFooter>
      </ModalContent>

      <OilTanksModal
        isOpen={oilTanksModal.isOpen}
        onClose={oilTanksModal.onClose}
        heatingSource="auxiliary"
        oilTanks={oilTanks}
        onSave={handleOilTanksSave}
        isUpdating={isUpdating}
      />
    </Modal>
  );
};

export default AuxiliaryHeatingModal;
